export const strings = {
  common: {
    nav: {
      myBooking: 'My bookings',
    },
    buttons: {
      viewDetails: 'View Details →',
      viewProperty: 'View property →',
      viewBooking: 'View booking details',
      viewAllPhotos: 'View all photos →',
      backToProperty: '← Back to property',
      backToBookings: '← Back to bookings',
      backToProperties: '← Back to properties',
      login: 'Login',
      logout: 'Logout',
      loading: 'Loading',
      menu: 'Menu',
    },
    actions: {
      close: 'Close',
      previous: 'Previous',
      next: 'Next',
    },
    labels: {
      payment: 'Payment',
      guestDetails: 'Guest details',
      stayDetails: 'Stay details',
      amountDueNow: 'Amount due now',
      bookingStatus: 'Booking status',
      guests: 'Guests',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      nights: 'Nights',
      email: 'Email',
      phone: 'Phone',
      name: 'Name',
    },
    footer: {
      tagline: 'Short-term rental booking and host management, with privacy-forward controls.',
      privacy: 'Privacy',
      privacyPolicy: 'Privacy Policy',
      ccpa: 'California Privacy (CCPA)',
      noticeAtCollection: 'Notice at Collection',
      doNotSell: 'Do Not Sell or Share My Personal Information',
      dataRequest: 'Privacy Request Form',
      contact: 'Contact',
      rightsReserved: (year: number, site: string) => `© ${year} ${site}. All rights reserved.`,
    },
    cookies: {
      title: 'Cookie preferences',
      intro:
        'We use cookies for analytics and marketing to improve the experience. Defaults are on unless you opt out.',
      privacyPolicy: 'Privacy Policy',
      doNotSell: 'Do Not Sell/Share',
      noticeAtCollection: 'Notice at Collection',
      essential: 'Essential',
      essentialHelper: 'Required for site functionality.',
      alwaysOn: 'Always on',
      analytics: 'Analytics',
      analyticsHelper: 'Helps us improve the booking flow.',
      marketing: 'Marketing',
      marketingHelper: 'Supports outreach and offers.',
      hidePreferences: 'Hide preferences',
      managePreferences: 'Manage preferences',
      savePreferences: 'Save preferences',
      acceptAll: 'Ok',
    },
    notice: {
      title: 'Notice at collection',
      summary: (categories: string) =>
        `We collect ${categories} to provide bookings, support, and improve the experience.`,
      reviewPrefix: 'Review the',
      noticeLink: 'Notice at Collection',
      and: 'and',
      privacyLink: 'Privacy Policy',
    },
    empty: {
      notAvailable: '—',
    },
    symbols: {
      disclosure: '→',
      rangeSeparator: '→',
      currency: '$',
      infinity: '∞',
    },
    chat: {
      buttonOpen: 'Chat',
      buttonClose: 'Close',
      headerWithProperty: (name: string) => `Chat about ${name}`,
      headerDefault: 'Chat with us',
      replyTime: 'We typically reply within a few hours.',
      noMessages: 'No messages yet.',
      messagePlaceholder: 'Write a message...',
      sendMessage: 'Send message',
      namePlaceholder: 'Your name',
      emailPlaceholder: 'Email',
      phonePlaceholder: 'Phone (optional)',
      helpPlaceholder: 'How can we help?',
      startChat: 'Start chat',
      loadMessagesError: 'Unable to load messages.',
      startChatError: 'Unable to start chat.',
      sendMessageError: 'Unable to send message.',
    },
    calendar: {
      daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      unavailableDates: 'Unavailable dates',
      minimumNights: (count: number) => `Minimum ${count} nights`,
      leadTime: (days: number) => `Requires ${days} day${days === 1 ? '' : 's'} notice`,
    },
  },
  screens: {
    home: {
      heroTitle: 'Find Your Perfect Getaway',
      heroSubtitle:
        'Discover unique vacation rentals for your next adventure. Book directly with confidence.',
      availableProperties: 'Available Properties',
      propertiesFound: (count: number) => `${count} properties found`,
      noPropertiesTitle: 'No properties available at the moment.',
      noPropertiesSubtitle: 'Please check back later or contact us for more information.',
      bedrooms: (count: number) => (count === 1 ? 'Bedroom' : 'Bedrooms'),
      guests: 'guests',
      beds: 'beds',
      baths: 'baths',
      perNight: '/ night',
    },
    login: {
      title: 'Sign in',
      subtitle: 'Use Google or Apple to continue.',
      heroTitle: 'Welcome back. Let’s keep your stays beautifully booked.',
      heroSubtitle: 'Sign in to continue.',
      heroImageAlt: 'Modern rental interior',
      providerTitle: 'Continue with your provider',
      providerSubtitle: 'We do not receive or store your credentials.',
      loadingProviders: 'Loading providers…',
      noProviders: 'No auth providers configured.',
      continueWith: (name: string) => `Continue with ${name}`,
      authorizedNotice: "By continuing, you agree you're authorized to access this account.",
      privacyPolicy: 'Privacy Policy',
      doNotSell: 'Do Not Sell/Share',
      noticeAtCollection: 'Notice at Collection',
    },
    error: {
      title: 'Something went wrong',
      subtitle: 'Try again or refresh the page.',
      retry: 'Try again',
    },
    sentryExample: {
      title: 'Sentry example',
      intro: 'Send a test error to confirm monitoring is configured.',
      trigger: 'Trigger test error',
      sending: 'Sending test error…',
      statusTitle: 'Status',
      sent: 'Test error sent. Check Sentry issues.',
      sentAt: 'Sent at',
      eventMessage: 'Sentry example message',
      errorMessage: 'Sentry example error',
    },
    property: {
      sections: {
        overview: 'Overview',
        amenities: 'Amenities',
        availability: 'Availability',
        location: 'Location',
        spaces: 'Spaces',
        rules: 'House Rules',
        platforms: 'Platforms',
        reviews: 'Reviews',
      },
      exploreSpaces: 'Explore the spaces',
      photosTitle: 'Photos',
      photosByRoom: 'Photos by room',
      totalPhotos: (count: number) => `${count} total photo${count === 1 ? '' : 's'}`,
      photosCount: (count: number) => `${count} photo${count === 1 ? '' : 's'}`,
      generalRoom: 'General',
      noPhotos: 'No photos available yet.',
      photoAlt: (room: string) => `${room} photo`,
      roomPhotoAlt: 'Room photo',
      closeGallery: 'Close',
      previousPhoto: 'Previous photo',
      nextPhoto: 'Next photo',
      photoIndex: (index: number, total: number) => `${index} / ${total}`,
      aboutStay: 'About this stay',
      fromNight: (amount: string, currency: string) => `From ${amount} / night · ${currency}`,
      perNight: '/ night',
      highlightsGuests: (guests: number, bedrooms: number, beds: number, baths: number) =>
        `${guests} guests · ${bedrooms} br · ${beds} beds · ${baths} baths`,
      highlightsTimes: (checkIn: string, checkOut: string) =>
        `Check-in ${checkIn} · Check-out ${checkOut}`,
      amenities: 'Amenities',
      availability: 'Availability',
      location: 'Location',
      spaces: 'Spaces',
      spacesEmpty: 'No space details yet.',
      spaceBedsLabel: 'Beds',
      spaceEnsuite: 'Ensuite bathroom',
      houseRules: 'House rules',
      platformsTitle: 'Platforms',
      reviewsTitle: 'Guest reviews',
      openInMaps: 'Open in Google Maps →',
      unavailableDates: 'Unavailable dates',
      selectDates: (min: number) => `Minimum ${min} nights`,
      mapPreviewUnavailable: 'Map preview unavailable. Use the link above to view on Google Maps.',
      houseRulesCheckInOut: (checkIn: string, checkOut: string) =>
        `Check-in after ${checkIn}, check-out by ${checkOut}`,
      houseRulesStayRange: (min: number, max: number) =>
        `Minimum stay: ${min} night(s); maximum stay: ${max} night(s)`,
      houseRulesQuietHours: 'Respect quiet hours and community guidelines',
      ratePlanHeader: 'Choose your rate',
      fullPrepay: 'Full prepay',
      payAtCheckIn: 'Pay at check-in',
      depositLabel: (percent: number, days: number) =>
        `${percent}% deposit, remainder ${days} days before checkin date`,
      freeCancelUntilDays: (days: number) =>
        `Free cancellation until ${days} days prior to checkin`,
      paymentSummaryFullPrepay: 'Full payment due at booking.',
      paymentSummaryPayAtCheckIn: 'Pay in full at check-in.',
      paymentSummaryDeposit: (percent: number, days: number) =>
        `${percent}% deposit now, remainder ${days} days before checkin date.`,
      paymentSummaryDefault: '50% deposit due now, remainder 14 days before checkin date.',
      cancellationSummaryNonRefundable: 'Non-refundable once booked.',
      cancellationSummaryFreeCancel: (days: number) =>
        `Free cancellation until ${days} days prior to checkin.`,
      cancellationSummaryPercent: (percent: number, days: number) =>
        `${percent}% refund until ${days} days before check-in.`,
      cancellationSummaryDefault: 'Moderate: full refund 5+ days before, 50% 1-5 days.',
      yourInformation: 'Your Information',
      requestToBook: 'Request to Book',
      bookingHelper: "You won't be charged yet. The host will review your request.",
      selectDatesTitle: 'Select dates',
      checkInLabel: 'Check-in',
      checkOutLabel: 'Check-out',
      guestsLabel: 'Number of Guests',
      guestsCount: (total: number, max: number) => `${total} / ${max}`,
      guests: {
        adults: 'Adults',
        adultsHint: 'Ages 13+',
        children: 'Children',
        childrenHint: 'Ages 2-12',
        infants: 'Infants',
        infantsHint: 'Under 2',
        pets: 'Pets',
        petsHint: 'Service or house pets',
      },
      guestControlDecrease: (label: string) => `Decrease ${label}`,
      guestControlIncrease: (label: string) => `Increase ${label}`,
      specialRequests: 'Special requests (optional)',
      datePlaceholder: 'Select from calendar',
      submitUnavailable: 'Pay at check-in',
      freeCancelUntil: (days: number) =>
        `Free cancellation until ${days} days prior to checkin`,
      stayLimits: (min: number, max: number) => `${min}-${max} night stay`,
      clearDates: 'Clear',
      priceBreakdownTitle: 'Price Breakdown',
      nightlyRate: 'Nightly rate',
      averageNightlyRate: 'Avg nightly rate',
      discount: 'Discount',
      cleaningFee: 'Cleaning fee',
      serviceFee: 'Service fee',
      taxes: 'Taxes',
      total: 'Total',
      paymentCancellation: 'Payment & cancellation',
      bookingRequestTitle: 'Booking Request Submitted!',
      bookingRequestCopy:
        "Your booking request has been sent to the host. You'll receive an email once it's reviewed.",
      calculateQuoteError: 'Failed to calculate quote',
      submitBookingError: 'Failed to submit booking request',
      processing: 'Processing...',
      mobileBookNow: 'Book Now',
      mobileBookTitle: 'Book your stay',
      mobileSelectDates: 'Select dates',
      mobileSelectGuests: 'Select guests',
      mobileRateDetails: 'Rate details',
      mobileTotalLabel: 'Total price',
      mobilePricePerNight: 'Price / night',
      mobileDone: 'Done',
      mobileClose: 'Close',
      guestInfoTitle: 'Your Information',
      firstNamePlaceholder: 'First Name',
      lastNamePlaceholder: 'Last Name',
      emailPlaceholder: 'Email',
      phonePlaceholder: 'Phone',
      rooms: {
        guests: 'Guests',
        bedrooms: 'Bedrooms',
        beds: 'Beds',
        baths: 'Baths',
      },
    },
    bookingLookup: {
      title: 'Verify your details to see all of your reservations.',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      phone: 'Phone',
      button: 'Find booking',
      loggedInTitle: 'Welcome back',
      loggedInSubtitle: 'Your bookings are listed below.',
    },
    bookingDetail: {
      title: 'My Booking',
      reviewTitle: 'Review your booking',
      accessTitle: 'Access your bookings',
      searchLabel: 'Search bookings',
      searchPlaceholder: 'Search by property, booking, guest, or status',
      resultsLabel: (shown: number, total: number) => `Showing ${shown} of ${total}`,
      noSearchResults: 'No bookings match that search.',
      tableHeaders: {
        booking: 'Booking',
        stay: 'Stay',
        status: 'Status',
        guests: 'Guests',
        action: 'Details',
      },
      expandRow: 'View',
      collapseRow: 'Hide',
      verifyTitle: 'Verify your booking',
      verifyThisBooking: 'Verify your details to view this booking.',
      verifyAllBookings: 'Verify your details to see all of your reservations.',
      loginButton: 'Login',
      lookupButton: 'Find booking',
      checking: 'Checking...',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      phone: 'Phone',
      noBookingsAccount: 'No bookings found for this account.',
      noBookingsDetails: 'No bookings found with those details.',
      noBookingsMatched: 'No bookings matched your details.',
      verifyError: 'Unable to verify your booking.',
      loadBookingsError: 'Unable to load bookings.',
      bookingId: (id: string) => `Booking ID: ${id}`,
      status: 'Booking',
      statusLabels: {
        PENDING: 'Pending',
        APPROVED: 'Approved',
        CONFIRMED: 'Confirmed',
        COMPLETED: 'Completed',
        CANCELLED: 'Cancelled',
        EXPIRED: 'Expired',
        REFUNDED: 'Refunded',
        PARTIALLY_REFUNDED: 'Partially refunded',
        REJECTED: 'Rejected',
      },
      refundOutside: 'Outside the refund window. No refund will be issued.',
      refundNoPayment: 'No payment has been collected yet. Refunds apply after payment is captured.',
      refundPreviewError: 'Unable to load refund details.',
      cancelTitle: 'Cancel booking',
      cancelConfirm: 'Cancel booking request',
      cancelSuccess: 'Booking cancelled. You will receive a confirmation email.',
      cancelReason: 'Cancelled by guest request',
      refundEstimate: 'Estimated refund if cancelled today',
      refundRate: (percent: number) => `Refund rate: ${percent}%`,
      viewProperty: 'View property →',
      nightlyRate: 'Nightly rate',
      avgNightlyRate: 'Avg nightly rate',
      stayDetails: 'Stay details',
      guestDetails: 'Guest details',
      specialRequests: 'Special requests',
      ratePlan: 'Rate plan',
      cancellationReasonLabel: 'Cancellation reason',
      lockAccessTitle: 'Lock access',
      lockAccessEmpty: 'No lock access codes available yet.',
      lockAccessDevice: 'Lock',
      lockAccessCode: 'Access codes',
      lockAccessWindow: 'Access window',
      lockAccessStatus: 'Status',
      lockAccessPending: 'Pending',
      pricing: 'Pricing',
      subtotal: 'Subtotal',
      totalCharged: 'Total charged',
      totalRefunded: 'Total refunded',
      paymentDue: 'Payment due',
      paymentHistory: 'Payment history',
      paymentHistoryEmpty: 'No payment activity yet.',
      paymentStatusLabel: 'Status',
      paymentTypeLabel: 'Type',
      paymentAmountLabel: 'Amount',
      paymentDueLabel: 'Due',
      paymentRefundLabel: 'Refund',
      paymentRefundedLabel: 'Refunded',
      paymentRefundedOnLabel: 'Refunded on',
      payNow: 'Pay now',
      manageBooking: 'Manage booking',
      manageBookingCopy: 'You can cancel this booking if your plans change.',
      needHelp: 'Need help?',
      needHelpCopy: 'Reply to your booking email for support or reach out to the host directly.',
      cancelModalTitle: 'Cancel booking',
      cancelModalSubtitle: 'Review your refund',
      close: 'Close',
      calculatingRefund: 'Calculating refund…',
      estimatedRefund: 'Estimated refund',
      originalTotal: 'Original total',
      keepBooking: 'Keep booking',
      confirmCancellation: 'Confirm cancellation',
      cancelling: 'Cancelling...',
      cancelError: 'Unable to cancel booking.',
    },
    bookingPay: {
      paymentTitle: 'Complete your booking',
      paymentIntro: (name?: string) => `Booking for ${name ?? 'your stay'}`,
      paymentRequired: 'Payment required to confirm your reservation.',
      paymentNone: 'No payment due.',
      paymentNoneNow: 'No payment due right now.',
      dueBy: (date: string) => `Due by ${date}`,
      holdNotice: (amount: string, date: string) =>
        `A ${amount} authorization hold for incidentals will be placed on ${date} and released after checkout if no damages are reported.`,
      paymentHeading: 'Payment',
      paymentStatusLabel: 'Payment status',
      paymentWindowExpired: 'Payment window expired',
      paymentCompleted: 'Payment is already completed for this booking.',
      awaitingApproval: 'This booking is awaiting host approval.',
      bookingCancelled: 'This booking was cancelled.',
      paymentNotRequired: 'Payment is not required for this booking right now.',
      paymentFailed: 'Payment failed.',
      paymentConfirmFailed: 'Payment confirmed but booking update failed.',
      paymentDetailsError: 'Please check your payment details.',
      paymentStartError: 'Unable to start payment.',
      paymentStatus: (status: string) => `Payment status: ${status}`,
      loadingPaymentForm: 'Loading payment form…',
      missingPublishableKey:
        'Stripe publishable key is missing. Set NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY.',
      paymentSuccess: 'Payment received. Your booking is now confirmed.',
      paidWith: (brand: string, last4: string) => `Paid with ${brand}${last4}.`,
      viewBookingDetails: 'View booking details',
      processing: 'Processing…',
      payAmount: (amount: string) => `Pay ${amount}`,
      paymentExpiredTitle: 'Payment window expired',
      paymentExpiredCopy:
        'The payment window for this booking has passed. You can reattempt the booking to send a new request to the host.',
      reattemptTitle: 'Reattempt booking',
      reattemptCopy: (date: string) => `Payment was due by ${date}.`,
    },
    bookingGate: {
      heading: 'Verify to continue',
      title: 'Confirm your booking details to access this payment link.',
      login: 'Login',
      formTitle: 'Verify your booking',
      firstNamePlaceholder: 'First name',
      lastNamePlaceholder: 'Last name',
      emailPlaceholder: 'Email',
      phonePlaceholder: 'Phone',
      submit: 'Continue to payment',
      submitting: 'Checking...',
      success: 'Verification successful. Loading payment details...',
      error: 'Unable to verify booking details. Please try again.',
    },
    reattempt: {
      button: 'Reattempt booking',
      loading: 'Reattempting…',
      success: 'New booking request sent.',
      error: 'Unable to reattempt booking.',
      missingId: 'Reattempted booking, but no booking ID was returned.',
    },
    admin: {
      dashboardTitle: 'Admin dashboard',
      themeTitle: 'Theme settings',
      propertiesTitle: 'Properties',
      bookingsTitle: 'Bookings',
      hostsTitle: 'Hosts',
      adminsTitle: 'Admins',
      privacyTitle: 'Privacy requests',
      chatTitle: 'Chat inbox',
      dashboard: {
        title: 'Dashboard',
        subtitle: 'Overview of your properties and bookings.',
        propertiesCard: 'Properties',
        pendingBookingsCard: 'Pending bookings',
        approvedBookingsCard: 'Approved/Confirmed',
        properties: 'Properties',
        propertiesListed: (count: number) => `${count} listed`,
        viewAll: 'View all →',
        noProperties: 'No properties in scope.',
        upcomingBookings: 'Upcoming bookings',
        upcomingScope: 'Next 6 in scope',
        noUpcoming: 'No upcoming bookings.',
        nightlySummary: (rate: number, guests: number) => `$${rate} / night · ${guests} guests`,
      },
      hosts: {
        title: 'Hosts',
        subtitle: 'Invite hosts and assign properties.',
        inviteTitle: 'Invite or update host',
        inviteSubtitle: 'Assign properties to a host email.',
        propertiesLabel: 'Properties',
        firstNamePlaceholder: 'First name',
        lastNamePlaceholder: 'Last name',
        emailPlaceholder: 'host@example.com',
        phonePlaceholder: 'Phone (optional)',
        propertySearchPlaceholder: 'Search properties by name or address',
        hostSearchPlaceholder: 'Search hosts by name or email',
        saveHost: 'Save host',
        savingHost: 'Saving...',
        editHost: 'Edit',
        removeHost: 'Remove',
        totalHosts: (count: number) => `${count} total`,
        noPropertiesLabel: 'No properties',
        noPropertyMatches: 'No properties match that search.',
        listTitle: 'Hosts',
        noHosts: 'No hosts match that search.',
        hostLabel: 'Host',
        associatedProperties: 'Associated properties',
        noAssociatedProperties: 'No properties assigned.',
        propertiesTotal: (count: number) => `${count} total`,
        addressNotSet: 'Address not set',
        dbMissing:
          'Host/Property tables not available yet. Run Prisma migrations/generate before using this page.',
        saveError: 'Failed to save host',
        deleteError: 'Failed to delete host',
      },
      theme: {
        title: 'Theme',
        subtitle: 'Control the public-facing color palette (CSS variables).',
        presets: 'Presets',
        presetsLimit: 'Limit reached — pick a preset to update.',
        noPresets: 'No presets yet. Save one to get started.',
        current: 'Current',
        selected: 'Selected',
        applyHint: 'Apply to edit, then save as your own preset.',
        presetName: 'Preset name',
        livePreview: 'Live preview',
        cardTitle: 'Card title',
        accentChip: 'Accent chip',
        accentChipHint: 'Surfaces that rely on accent colors',
        dbMissing:
          'Theme table not in client/migrations. Run `npx prisma migrate dev` (or db push/generate) to enable saving.',
        dbError:
          'Theme query failed. Ensure migrations are applied and Prisma client is regenerated.',
        dbHint: 'Starter palettes still load for preview; saving requires the ThemeConfig table.',
        fieldLabels: {
          primary: 'Primary',
          primaryFg: 'On primary',
          ghost: 'Ghost',
          ghostFg: 'On ghost',
          background: 'Background',
          foreground: 'Foreground',
          accent: 'Accent',
          accentFg: 'On accent',
          card: 'Card',
          cardFg: 'On card',
          ring: 'Ring',
          border: 'Border',
        },
        fieldHelp: {
          primary: 'Buttons, focus states',
          primaryFg: 'Text/icons on primary surfaces',
          ghost: 'Neutral surfaces (ghost buttons, chips)',
          ghostFg: 'Text/icons on ghost surfaces',
          background: 'Page background',
          foreground: 'Body text',
          accent: 'Cards and accent surfaces',
          accentFg: 'Text/icons on accent',
          card: 'Card backgrounds',
          cardFg: 'Card text',
          ring: 'Focus ring and outlines',
          border: 'Borders and inputs',
        },
        presetCount: (count: number) => `${count}/20 saved. Current preset is highlighted.`,
        newPreset: 'New preset',
        deletePreset: 'Delete preset',
        deleting: 'Deleting…',
        resetDefaults: 'Reset to defaults',
        presetLabel: 'Preset',
        currentTheme: 'Current theme',
        updatedAt: (date: string) => `Updated ${date}`,
        suggestionsClose: 'Close suggestions',
        suggestionsOpen: 'Suggested palettes',
        applySuggested: 'Apply',
        paletteLabel: 'Palette',
        presetNamePlaceholder: 'e.g. Coastal light, Dark luxe, Autumn',
        livePreviewBody: 'See how body copy and container colors look together.',
        primaryButton: 'Primary',
        ghostButton: 'Ghost',
        ghostChip: 'Ghost chip',
        mutedChip: 'Muted',
        savePalette: 'Save palette',
        saving: 'Saving…',
        saveMessage: 'Preset saved.',
        saveMessageHint: 'Saved. Refresh the site to see the new palette.',
        deleteMessage: 'Preset deleted.',
        deleteError: 'Failed to delete preset',
        saveError: 'Failed to save theme',
        presetLimitReached: 'Preset limit reached (20). Select a preset to update.',
        resetMessage: 'Reset to defaults (not yet saved).',
        editNewPresetMessage: 'Editing as a new preset (unsaved).',
        loadStarter: (name?: string) =>
          `Loaded starter palette${name ? ` \"${name}\"` : ''}. Save to keep it.`,
        valuesNote:
          'Values are stored as HSL strings for Tailwind CSS variables. Sliders are RGB for quick visual tweaking.',
      },
      bookings: {
        title: 'Bookings',
        subtitle: 'Latest bookings in your scope.',
        filters: 'Filters',
        filterButton: 'Filter',
        clear: 'Clear',
        close: 'Close',
        anyTime: 'Any time',
        rangePlaceholder: '…',
        bookingDate: 'Booking date',
        stayDate: 'Stay date',
        status: 'Status',
        property: 'Property',
        allProperties: 'All properties',
        noMatches: 'No matches.',
        guestEmail: 'Guest email',
        guestEmailPlaceholder: 'guest@example.com',
        propertySearchPlaceholder: 'Search property name or address',
        clearFilters: 'Clear filters',
        applyFilters: 'Apply',
        done: 'Done',
        resultsCount: (count: number) => `${count} result${count === 1 ? '' : 's'}`,
        bookedHeader: 'Booked',
        propertyHeader: 'Property',
        guestHeader: 'Guest',
        datesHeader: 'Dates',
        total: 'Total',
        actions: 'Actions',
        noBookings: 'No bookings in scope.',
        viewBooking: (name: string) => `View booking for ${name}`,
        statusLabels: {
          all: 'All statuses',
          PENDING: 'Pending',
          APPROVED: 'Approved',
          CONFIRMED: 'Confirmed',
          COMPLETED: 'Completed',
          REFUNDED: 'Refunded',
          PARTIALLY_REFUNDED: 'Partially refunded',
          CANCELLED: 'Cancelled',
          REJECTED: 'Rejected',
          EXPIRED: 'Expired',
        },
        actionsUi: {
          approve: 'Approve',
          reject: 'Reject',
          cancel: 'Cancel',
          actionFailed: 'Action failed',
          cancelTitle: 'Cancel booking',
          cancelReasonPrompt: 'Provide a reason for cancellation.',
          cancelRefundPrompt: 'Choose the refund amount for this cancellation.',
          maxRefundable: (amount: string) => `Max refundable: ${amount}`,
          refundAmountLabel: (amount: string) => `Refund amount (max ${amount})`,
          cancelPlaceholder: 'Reason for cancellation (min 10 characters)',
          refundAmountInvalid: 'Refund amount must be a valid number.',
          reasonTooShort: 'Reason must be at least 10 characters.',
          keepBooking: 'Keep booking',
          confirmCancellation: 'Confirm cancellation',
          rejectReason: 'Rejected by admin',
        },
        refundUi: {
          issueRefund: 'Issue refund',
          refundIntro: 'This issues a refund without cancelling the booking.',
          refundAmount: (amount: string) => `Refund amount: ${amount}`,
          refundAmountLabel: (amount: string) => `Refund amount (max ${amount})`,
          refundReasonPlaceholder: 'Refund reason (optional)',
          refundAmountInvalid: 'Enter a valid refund amount.',
          refundError: 'Unable to issue refund.',
          keepBooking: 'Keep booking',
          processing: 'Processing…',
          confirmRefund: 'Confirm refund',
        },
        detail: {
          header: 'Booking',
          stayDetails: 'Stay details',
          accessTimes: 'Access times',
          accessTimesHelper: 'Adjusting these times updates the guest lock access window.',
          checkInTimeLabel: 'Check-in time',
          checkOutTimeLabel: 'Check-out time',
          timesSave: 'Save times',
          timesSaving: 'Saving...',
          timesSaved: 'Access times updated.',
          timesReset: 'Using property default times.',
          timesResetButton: 'Use property times',
          timesInvalid: 'Enter valid check-in and check-out times.',
          timesError: 'Unable to update access times.',
          accessTimesDefault: (checkIn: string, checkOut: string) =>
            `Property defaults: ${checkIn} / ${checkOut}.`,
          lockAccessTitle: 'Lock access',
          lockAccessEmpty: 'No lock access codes assigned.',
          lockAccessDevice: 'Lock',
          lockAccessCode: 'Access codes',
          lockAccessWindow: 'Access window',
          lockAccessStatus: 'Status',
          lockAccessPending: 'Pending',
          guestDetails: 'Guest details',
          specialRequests: 'Special requests',
          ratePlan: 'Rate plan',
          defaultRatePlan: 'Default rate',
          payments: 'Payments',
          received: 'Received',
          noPaymentsReceived: 'No payments received yet.',
          pending: 'Pending',
          noPendingPayments: 'No pending payments.',
          refunds: 'Refunds',
          noRefunds: 'No refunds processed.',
          refundEntryLabel: 'Refund',
          refundPaymentTypeLabel: 'Payment type',
          receivedStatus: 'received',
          paymentIntentId: 'Payment intent',
          paymentRefundedLabel: 'Refunded',
          paymentRefundedOnLabel: 'Refunded on',
          pricing: 'Pricing',
          nightlyRate: 'Nightly rate',
          avgNightlyRate: 'Avg nightly rate',
          cleaningFee: 'Cleaning fee',
          serviceFee: 'Service fee',
          taxes: 'Taxes',
          total: 'Total',
          totalCharged: 'Total charged',
          totalRefunded: 'Total refunded',
          cancellationRequested: 'Cancellation requested',
          cancellationRequestedBy: 'Cancellation requested by',
          requesterEmail: 'Requester email',
          cancellationWindow: 'Cancellation window',
          refundInitiated: 'Refund initiated',
          fullRefundInitiated: 'Full refund initiated',
          additionalCredits: 'Additional credits',
          refunded: 'Refunded',
          refundProvided: 'Refund provided',
          refundEstimate: 'Estimated refund if cancelled today',
          refundRate: (percent: number) => `Refund rate: ${percent}%`,
          outsideRefundWindow: 'Outside the refund window. No refund will be issued.',
          actions: 'Actions',
          backToBookings: '← Back to bookings',
          yes: 'Yes',
          no: 'No',
          cancellationWindowOverride: 'Overridden (host/admin)',
          cancellationWindowWithin: 'Within window',
          cancellationWindowOutside: 'Outside window',
        },
        incidentals: {
          title: 'Incidentals hold',
          statusNotConfigured: 'not configured',
          noHoldConfigured: 'No incidentals hold is configured for this booking.',
          holdAmount: 'Hold amount',
          remainingHold: 'Remaining hold',
          authorized: 'Authorized',
          releaseAfterCheckout: 'Release after checkout',
          captured: 'Captured',
          released: 'Released',
          holdFailed: 'Incidentals hold failed. Please contact the guest to update payment details.',
          retryHold: 'Retry hold',
          retrySuccess: 'Incidentals hold retried.',
          retryFailed: 'Unable to retry incidentals hold.',
          historyTitle: 'Incidentals history',
          imageLabel: (index: number) => `Image ${index}`,
          createdByFallback: 'host',
          confirmTitle: 'Confirm incidentals charge',
          confirmAmount: (amount: string) => `Charge ${amount} from the hold.`,
          imagesAttached: (count: number) => `${count} image${count === 1 ? '' : 's'} attached.`,
          back: 'Back',
          confirmCharge: 'Confirm charge',
          reportTitle: 'Report incidentals',
          amountLabel: (currency: string) => `Amount (${currency})`,
          descriptionLabel: 'Description',
          descriptionPlaceholder: 'Describe the incidentals...',
          imagesLabel: 'Images (required)',
          imagePlaceholder: 'Paste image URL',
          addImage: 'Add image',
          removeImage: 'Remove',
          uploadImages: 'Upload images',
          uploadingImages: 'Uploading…',
          reviewCharge: 'Review charge',
          submitting: 'Submitting incidentals charge…',
          chargeError: 'Unable to charge incidentals.',
          chargeSuccess: 'Incidentals charge captured from the hold.',
          reportLocked: (date: string) => `Reporting opens on ${date}.`,
        },
      },
      properties: {
        title: 'Properties',
        subtitle: 'Manage properties in your scope.',
        name: 'Name',
        location: 'Location',
        capacity: 'Capacity',
        rate: 'Rate',
        status: 'Status',
        view: 'View',
        addProperty: '+ Add property',
        searchPlaceholder: 'Search properties by name or address',
        editProperty: (name: string) => `Edit ${name}`,
        capacityValue: (guests: number, bedrooms: number, beds: number, baths: number) =>
          `${guests} guests · ${bedrooms} br · ${beds} beds · ${baths} baths`,
        perNight: '/ night',
        active: 'Active',
        inactive: 'Inactive',
        noMatches: 'No properties match that search.',
        editTitle: 'Edit Property',
        addTitle: 'Add Property',
        addSubtitle: 'Create a new property listing.',
        themeUnavailable:
          'Theme presets are unavailable right now. Apply migrations/generate to enable theme selection.',
        availabilityCalendar: 'Availability calendar',
        availability: {
          title: 'Block dates',
          startDate: 'Start date',
          endDate: 'End date',
          reasonOptional: 'Reason (optional)',
          blockButton: 'Block dates',
          blockedTitle: 'Blocked dates',
          managedExternally: 'Managed externally',
          unblock: 'Unblock',
          noBlocks: 'No blocks.',
          saving: 'Saving...',
          blockError: 'Failed to block',
          unblockError: 'Failed to unblock',
        },
        minNights: {
          title: 'Min nights overrides',
          startLabel: 'Start',
          endLabel: 'End',
          minNightsLabel: 'Min nights',
          addOverride: 'Add override',
          remove: 'Remove',
          saving: 'Saving...',
          noOverrides: 'No overrides.',
          saveError: 'Failed to save',
          deleteError: 'Failed to delete',
          overrideSummary: (minNights: number) => `Min ${minNights} nights`,
        },
        rateOverrides: {
          title: 'Nightly rate overrides',
          startLabel: 'Start',
          endLabel: 'End',
          rateLabel: 'Rate',
          addOverride: 'Add override',
          remove: 'Remove',
          saving: 'Saving...',
          noOverrides: 'No overrides.',
          saveError: 'Failed to save',
          deleteError: 'Failed to delete',
          csvTitle: 'Import CSV',
          csvHint: 'Format: date,price',
          csvUploading: 'Importing CSV...',
          csvError: 'Failed to import CSV',
          csvEmpty: 'No valid rows found.',
          csvRowError: (row: number) => `Row ${row}: invalid date or price`,
        },
        ratePlans: {
          title: 'Rate plans',
          details: 'Details',
          hide: 'Hide',
          edit: 'Edit',
          enable: 'Enable',
          disable: 'Disable',
          delete: 'Delete',
          description: 'Description',
          adjustment: 'Adjustment',
          refundLadder: 'Refund ladder',
          cancellationWindow: 'Cancellation window',
          leadDays: 'Lead days',
          stayNights: 'Stay nights',
          channel: 'Channel',
          noPlans: 'No rate plans yet.',
          editPlan: 'Edit rate plan',
          addPlan: 'Add rate plan',
          nameLabel: 'Name',
          activeLabel: 'Active',
          activeOption: 'Active',
          inactiveOption: 'Inactive',
          descriptionLabel: 'Description',
          adjustmentTypeLabel: 'Adjustment type',
          adjustmentPercent: 'Percent',
          adjustmentFixed: 'Fixed ($/night)',
          adjustmentValueLabel: (kind: 'percent' | 'fixed') =>
            `${kind === 'percent' ? 'Percent' : 'Amount'} (${kind === 'percent' ? '%' : '$'})`,
          cancellationWindowLabel: 'Cancellation window (days)',
          minLeadDaysLabel: 'Minimum lead days',
          maxLeadDaysLabel: 'Maximum lead days',
          minStayNightsLabel: 'Minimum stay nights',
          maxStayNightsLabel: 'Maximum stay nights',
          paymentRuleLabel: 'Payment rule',
          paymentPayAtCheckIn: 'Pay at check-in',
          paymentDeposit: 'Deposit',
          paymentFullPrepay: 'Full prepay',
          channelAny: 'Any',
          channelDirect: 'Direct',
          channelOta: 'OTA',
          channelBoth: 'Both',
          depositPercentLabel: 'Deposit %',
          remainderDaysLabel: 'Remainder days before',
          refundLadderLabel: 'Refund ladder',
          addRule: 'Add rule',
          noRefundRules:
            'No rules yet. If empty, the cancellation window will create a single 100% refund rule.',
          cutoffPlaceholder: 'Cutoff days',
          refundPlaceholder: 'Refund %',
          removeRule: 'Remove',
          saveChanges: 'Save changes',
          addPlanButton: 'Add plan',
          cancelEdit: 'Cancel',
          saving: 'Saving...',
          saveError: 'Failed to save rate plan',
          updateError: 'Failed to update rate plan',
          deleteError: 'Failed to delete rate plan',
          nonRefundable: 'Non-refundable',
          refundLadderCount: (count: number) => `Refund ladder (${count})`,
          leadSummary: (min: number | string, max: number | string) => `Lead ${min}-${max}d`,
          staySummary: (min: number | string, max: number | string) => `Stay ${min}-${max}n`,
          channelSummary: (channel: string) => `Channel ${channel}`,
          depositSummary: (percent: number) => `${percent}% deposit`,
          fullPrepaySummary: 'Full prepay',
          payAtCheckInSummary: 'Pay at check-in',
          refundRuleSummary: (percent: number, days: number) => `${percent}% ≥ ${days}d`,
          cancellationWindowValue: (days: number) => `${days} days`,
          leadDaysValue: (min: number | string, max: number | string) => `${min} - ${max} days`,
          stayNightsValue: (min: number | string, max: number | string) => `${min} - ${max} nights`,
          descriptionEmpty: '—',
        },
        hostAssignments: {
          title: 'Hosts',
          noneAssigned: 'No hosts assigned yet.',
          addExisting: 'Add existing host',
          selectHost: 'Select a host',
          assignHost: 'Assign host',
          inviteNew: 'Invite new host',
          firstNamePlaceholder: 'First name',
          lastNamePlaceholder: 'Last name',
          emailPlaceholder: 'Email',
          inviteHost: 'Invite host',
          removeHost: 'Remove',
          assignError: 'Unable to assign host.',
          inviteError: 'Unable to invite host.',
          removeError: 'Unable to remove host.',
        },
        locks: {
          title: 'Smart locks',
          subtitle: 'Connect lock devices to automate guest access.',
          connectTitle: 'Provider connection',
          connectDescription:
            'Connect your provider account, then sync devices into this property.',
          connectButton: 'Connect Seam',
          connecting: 'Connecting...',
          connectLaunched: 'Connection opened in a new tab.',
          connectComplete: 'Connection authorized. Sync devices to add locks.',
          connectFailed: 'Connection failed. Try again.',
          connectError: 'Unable to start connection.',
          connectCompleteError: 'Unable to finish connection.',
          connectMissing: 'Set SEAM_API_KEY (or SEAM_ACCESS_TOKEN) to enable Seam connections.',
          syncButton: 'Sync devices',
          syncing: 'Syncing...',
          syncError: 'Unable to sync devices.',
          syncSummary: (added: number, updated: number, conflicts: number) =>
            `Synced ${added} new, ${updated} updated, ${conflicts} conflicts.`,
          connectedAccount: 'Connected account',
          notConnected: 'Not connected',
          lastSynced: (value: string) => `Last synced ${value}`,
          addTitle: 'Add lock device',
          deviceIdPlaceholder: 'Device ID',
          namePlaceholder: 'Display name',
          activeLabel: 'Active',
          addButton: 'Add lock',
          saveButton: 'Save',
          removeButton: 'Remove',
          addError: 'Unable to add lock.',
          updateError: 'Unable to update lock.',
          removeError: 'Unable to remove lock.',
          none: 'No locks assigned to this property.',
          activeStatus: 'Active',
          inactiveStatus: 'Inactive',
          lockStatusLabel: 'Lock status',
          powerStatusLabel: 'Power status',
          lockStatusValues: {
            locked: 'Locked',
            unlocked: 'Unlocked',
            open: 'Open',
            unknown: 'Unknown',
          },
          powerStatusUnknown: 'Unknown',
          batteryLevel: (value: number) => `Battery ${value}%`,
          batteryState: (value: string) => `Battery ${value}`,
          powerSource: (value: string) => `Power ${value}`,
          providers: {
            seam: 'Seam',
            yale: 'Yale',
            august: 'August',
            schlage: 'Schlage',
            kwikset: 'Kwikset',
            latch: 'Latch',
            ttlock: 'TTLock',
            salto: 'Salto',
            unknown: 'Unknown',
          },
        },
        ical: {
          title: 'iCal sync',
          exportLabel: 'Export URL (share with external channels)',
          copied: 'Copied',
          copy: 'Copy',
          editFeed: 'Edit feed',
          addFeed: 'Add feed',
          feedNameLabel: 'Feed name',
          feedUrlLabel: 'Feed URL',
          activeLabel: 'Active feed',
          saving: 'Saving...',
          updateFeed: 'Update feed',
          addFeedButton: 'Add feed',
          cancel: 'Cancel',
          saveError: 'Failed to save feed',
          syncError: 'Sync failed',
          updateError: 'Failed to update feed',
          deleteError: 'Failed to delete feed',
          importedTitle: 'Imported feeds',
          statusSuccess: 'Success',
          statusError: 'Error',
          statusNever: 'Never',
          lastSync: (date: string) => `Last sync: ${date}`,
          notSynced: 'Not synced yet',
          disabled: 'Disabled',
          sync: 'Sync',
          edit: 'Edit',
          enable: 'Enable',
          disable: 'Disable',
          delete: 'Delete',
          noFeeds: 'No feeds connected yet.',
        },
        images: {
          title: 'Media',
          pasteUrl: 'Paste image URL',
          addUrl: 'Add URL',
          roomPlaceholder: 'Room (e.g., Living room, Bedroom 1)',
          captionPlaceholder: 'Caption (optional)',
          roomLabelPlaceholder: 'Room label',
          untitled: 'Untitled',
          imageAlt: (index: number) => `Image ${index}`,
          primary: 'Primary',
          setPrimary: 'Set primary',
          hero: 'Hero image',
          setHero: 'Set hero',
          showInGallery: 'Show in gallery',
          moveUp: 'Up',
          moveDown: 'Down',
          remove: 'Remove',
          emptyState: 'Upload images or paste URLs to add media.',
          storageHint:
            'Files are stored as data URLs in the database; for production, connect a storage provider.',
        },
        editForm: {
          name: 'Name',
          description: 'Description',
          baseNightlyRate: 'Base nightly rate',
          cleaningFee: 'Cleaning fee',
          taxRate: 'Tax rate (%)',
          serviceFeeRate: 'Service fee (%)',
          maxGuests: 'Max guests (people sleep)',
          bedrooms: 'Bedrooms (rooms)',
          beds: 'Beds',
          bathrooms: 'Bathrooms',
          maxPets: 'Max pets',
          timeZone: 'Time zone',
          minNights: 'Min nights',
          maxNights: 'Max nights',
          defaultCancellationPolicy: 'Default cancellation policy',
          policyOptions: {
            flexible: 'Flexible',
            moderate: 'Moderate',
            strict: 'Strict',
          },
          theme: 'Theme',
          useGlobalTheme: 'Use global theme',
          useGlobalThemeHint: 'Applies the site-wide palette.',
          globalLabel: 'Global',
          noThemes: 'No saved theme presets yet.',
          amenities: 'Amenities (one per line: category:name)',
          amenitiesPlaceholder: 'essential:WiFi\ncomfort:Air conditioning',
          spaces: 'Spaces',
          addSpace: 'Add space',
          noSpaces: 'No spaces added yet.',
          spaceNamePlaceholder: 'Bedroom name',
          spaceBedsPlaceholder: 'Beds (e.g., 1 king, 2 twin)',
          spaceEnsuite: 'Ensuite bathroom',
          removeSpace: 'Remove',
          houseRules: 'House rules (one per line)',
          houseRulesPlaceholder: 'No smoking\nQuiet hours after 10 PM',
          active: 'Active',
          homeFeature: 'Set as home page property',
          saving: 'Saving...',
          saveChanges: 'Save changes',
          saved: 'Saved',
          saveError: 'Failed to save',
          pricePlaceholder: '0.00',
        },
        createForm: {
          name: 'Name',
          currency: 'Currency',
          description: 'Description',
          baseNightlyRate: 'Base nightly rate',
          cleaningFee: 'Cleaning fee',
          taxRate: 'Tax rate (%)',
          serviceFeeRate: 'Service fee (%)',
          maxGuests: 'Max guests',
          bedrooms: 'Bedrooms',
          beds: 'Beds',
          bathrooms: 'Bathrooms',
          maxPets: 'Max pets',
          checkInTime: 'Check-in time',
          checkOutTime: 'Check-out time',
          timeZone: 'Time zone',
          minNights: 'Min nights',
          maxNights: 'Max nights',
          defaultCancellationPolicy: 'Default cancellation policy',
          policyOptions: {
            flexible: 'Flexible',
            moderate: 'Moderate',
            strict: 'Strict',
          },
          address: 'Address',
          street: 'Street',
          city: 'City',
          state: 'State',
          zip: 'ZIP',
          country: 'Country',
          amenities: 'Amenities (one per line: category:name)',
          amenitiesPlaceholder: 'essential:WiFi\ncomfort:Air conditioning',
          spaces: 'Spaces',
          addSpace: 'Add space',
          noSpaces: 'No spaces added yet.',
          spaceNamePlaceholder: 'Bedroom name',
          spaceBedsPlaceholder: 'Beds (e.g., 1 king, 2 twin)',
          spaceEnsuite: 'Ensuite bathroom',
          removeSpace: 'Remove',
          houseRules: 'House rules (one per line)',
          houseRulesPlaceholder: 'No smoking\nQuiet hours after 10 PM',
          homeFeature: 'Set as home page property',
          createProperty: 'Create property',
          saving: 'Saving...',
          created: 'Property created',
          createError: 'Failed to create property',
          pricePlaceholder: '0.00',
        },
        reviews: {
          title: 'Platform reviews',
          subtitle: 'Add listing URLs and sync reviews into this property.',
          addSource: 'Add source',
          platformPlaceholder: 'Platform name (e.g., Airbnb)',
          urlPlaceholder: 'Listing URL',
          add: 'Add link',
          saving: 'Saving...',
          saveError: 'Failed to save platform link.',
          deleteError: 'Failed to remove platform link.',
          sync: 'Sync',
          synced: 'Reviews synced.',
          syncError: 'Failed to sync reviews.',
          delete: 'Remove',
          lastSynced: 'Last sync:',
          never: 'Never',
          empty: 'No platform links yet.',
          recent: 'Recent reviews',
          noReviews: 'No reviews synced yet.',
          anonymous: 'Anonymous',
          undated: 'No date',
          saved: 'Saved.',
        },
      },
      privacyRequests: {
        title: 'Privacy requests',
        subtitle: 'Manage CCPA/CPRA access, delete, and opt-out requests.',
        updateError: 'Failed to update request',
        unnamedRequester: 'Unnamed requester',
        noRequests: 'No privacy requests yet.',
        statusOptions: ['PENDING', 'VERIFIED', 'COMPLETED', 'REJECTED'],
      },
      chat: {
        title: 'Chat',
        subtitle: 'Support guests and answer questions quickly.',
        conversations: 'Conversations',
        total: (count: number) => `${count} total`,
        noRequests: 'No chat requests yet.',
        noMessages: 'No messages yet.',
        replyTime: 'We typically reply within a few hours.',
        generalSupport: 'General support',
        guestLabel: 'Guest',
        closeChat: 'Close chat',
        reopenChat: 'Reopen chat',
        writeReply: 'Write a reply...',
        selectConversation: 'Select a conversation',
        sendReply: 'Send reply',
        loadError: 'Unable to load messages.',
        sendError: 'Unable to send message.',
        statusError: 'Unable to update status.',
      },
      adminManager: {
        title: 'Admin Management',
        subtitle: 'Add or remove platform admins.',
        addTitle: 'Add admin',
        addSubtitle: 'Create or update an admin by email.',
        adminsTitle: 'Admins',
        noAdmins: 'No admins yet.',
        emailPlaceholder: 'email@example.com',
        roles: {
          superAdmin: 'SUPER_ADMIN',
          admin: 'ADMIN',
          viewer: 'VIEWER',
        },
        saving: 'Saving...',
        save: 'Save',
        remove: 'Remove',
        total: (count: number) => `${count} total`,
        addError: 'Failed to add admin',
        deleteError: 'Failed to delete admin',
      },
    },
    apiPlayground: {
      title: '🧪 API Playground',
      subtitle: 'Test the RentalManagement API endpoints',
      endpoints: 'Endpoints',
      requestTitle: 'Request',
      requestBodyTitle: 'Request Body (JSON)',
      send: 'Send',
      sending: 'Sending...',
      urlPlaceholder: '/api/...',
      jsonPlaceholder: '{ ... }',
      requestHint: '💡 Replace {id} with actual IDs. Get IDs from GET requests first.',
      responseTitle: 'Response',
      responsePlaceholder: 'Response will appear here...',
      statusLabel: (status: number) => `Status: ${status}`,
      emptyResponse: '(empty response)',
      errorPrefix: 'Error:',
      unknownError: 'Unknown error',
      methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
      quickReference: '📚 Quick Reference',
      quickProps: 'Properties',
      quickBookings: 'Bookings',
      quickQuote: 'Quote',
      quickWorkflow: 'Workflow',
      quickLists: {
        properties: [
          'GET /api/properties',
          'GET /api/properties/:id',
          'POST /api/properties',
          'PUT /api/properties/:id',
          'DELETE /api/properties/:id',
        ],
        bookings: [
          'GET /api/bookings',
          'POST /api/bookings',
          'POST /api/bookings/:id/approve',
          'POST /api/bookings/:id/reject',
          'POST /api/bookings/:id/cancel',
        ],
        quote: ['POST /api/quote'],
      },
      workflowSteps: [
        '1. Create property',
        '2. Get quote',
        '3. Create booking',
        '4. Approve/Reject',
        '5. Cancel if needed',
      ],
      endpointDescriptions: {
        listProperties: 'List all properties',
        getProperty: 'Get property by ID',
        createProperty: 'Create a property',
        updateProperty: 'Update a property',
        deleteProperty: 'Delete a property',
        quote: 'Get a quote for dates',
        listBookings: 'List all bookings',
        createBooking: 'Create a booking request',
        approveBooking: 'Approve a booking',
        rejectBooking: 'Reject a booking',
        cancelBooking: 'Cancel a booking',
      },
    },
    legal: {
      privacyTitle: 'Privacy Policy',
      ccpaTitle: 'CCPA Privacy Notice',
      doNotSellTitle: 'Do Not Sell or Share My Personal Information',
      noticeTitle: 'Notice at Collection',
      dataRequestTitle: 'Data Request',
      dataRequestForm: {
        success: 'Request received. We will follow up to verify and process your request.',
        fullName: 'Full name',
        email: 'Email',
        phone: 'Phone (optional)',
        requestType: 'Request type',
        details: 'Details (optional)',
        submit: 'Submit request',
        submitting: 'Submitting...',
        submitError: 'Failed to submit request',
        company: 'Company',
      },
      privacy: {
        lastUpdated: (date: string) => `Last updated: ${date}`,
        overviewTitle: 'Overview',
        overviewCopy:
          'This policy explains how we collect, use, and share personal information when you use our booking platform.',
        infoCollectTitle: 'Information we collect',
        whyUseTitle: 'Why we use personal information',
        sourcesTitle: 'Sources of information',
        sharingTitle: 'Sharing of information',
        sharingCopy:
          'We share information with trusted service providers as needed to operate the platform.',
        sharingSell: (sells: boolean, shares: boolean) =>
          `We ${sells ? 'may' : 'do not'} sell personal information and ${shares ? 'may' : 'do not'} share personal information for cross-context behavioral advertising.`,
        retentionTitle: 'Data retention',
        rightsTitle: 'Your privacy rights',
        rightsCopy:
          'California residents can request access, deletion, correction, and opt out of sale or sharing. Visit the CCPA page or submit a request directly.',
        contactTitle: 'Contact us',
        contactCopy: (email: string, address: string) =>
          `Email us at ${email} or write to ${address}.`,
      },
      notice: {
        title: 'Notice at Collection',
        intro: 'We collect the following categories of personal information when you use the site.',
        categoriesTitle: 'Categories collected',
        purposesTitle: 'Purposes',
        saleTitle: 'Sale or sharing',
        saleCopy: (sells: boolean, shares: boolean) =>
          `We ${sells ? 'may' : 'do not'} sell personal information and ${shares ? 'may' : 'do not'} share personal information for cross-context behavioral advertising.`,
      },
      doNotSell: {
        title: 'Do Not Sell or Share My Personal Information',
        intro:
          'Use this form to opt out of the sale or sharing of personal information. We will confirm your request.',
      },
      dataRequest: {
        title: 'Privacy Request',
        intro:
          'Submit a request to access, delete, correct, or opt out of sale/sharing of your personal information.',
      },
      ccpa: {
        title: 'California Privacy Rights (CCPA/CPRA)',
        intro: 'This page describes your rights and how to submit a request.',
        rightsTitle: 'Your rights',
        rights: [
          'Right to know what personal information we collect and share.',
          'Right to request deletion of personal information.',
          'Right to correct inaccurate personal information.',
          'Right to opt out of the sale or sharing of personal information.',
          'Right to non-discrimination for exercising your rights.',
        ],
        submitTitle: 'How to submit a request',
        submitCopy:
          'Use our online form, email us, or call our privacy line. We will verify your request before completing it.',
        submitButton: 'Submit a request',
        doNotSellButton: 'Do not sell or share',
        contactLine: (email: string, phone: string) =>
          phone ? `Email: ${email} · Phone: ${phone}` : `Email: ${email}`,
        saleTitle: 'Sale or sharing of personal information',
        saleCopy: (sells: boolean, shares: boolean) =>
          `We ${sells ? 'may' : 'do not'} sell personal information and ${shares ? 'may' : 'do not'} share personal information for cross-context behavioral advertising.`,
      },
    },
  },
} as const;
